import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useUiLocale } from "../context/UiLocaleContext";
import { useTranslation } from "../i18n/useTranslation";
import SiteFooter from "../components/SiteFooter";

const COMMON_WEAK_PASSWORDS = new Set([
  "password",
  "12345678",
  "qwerty",
  "letmein",
  "iloveyou",
  "admin",
  "welcome",
]);

const COUNTRY_VALUES = ["Israel", "USA", "Other"] as const;
type CountryValue = (typeof COUNTRY_VALUES)[number];

const validatePassword = (password: string, t: (key: string) => string): string | null => {
  const p = typeof password === "string" ? password : "";

  if (p.length < 10) return t("auth.signup.validation.minLength");
  if (p.length > 72) return t("auth.signup.validation.maxLength");
  if (COMMON_WEAK_PASSWORDS.has(p.trim().toLowerCase())) return t("auth.signup.validation.tooWeak");

  const hasUpper = /[A-Z]/.test(p);
  const hasLower = /[a-z]/.test(p);
  const hasNumber = /[0-9]/.test(p);
  const hasSpecial = /[^A-Za-z0-9]/.test(p);

  if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) {
    return t("auth.signup.validation.complexity");
  }

  return null;
};

const Signup: React.FC = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { uiLocale } = useUiLocale();

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState<"" | CountryValue>("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const passwordError = useMemo(() => validatePassword(password, t), [password, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!fullName.trim()) throw new Error(t("auth.signup.errors.fullNameRequired"));
      if (!email.trim()) throw new Error(t("auth.signup.errors.emailRequired"));
      if (!country || !COUNTRY_VALUES.includes(country as CountryValue)) {
        throw new Error(t("auth.signup.errors.countryRequired"));
      }
      if (!password) throw new Error(t("auth.signup.errors.passwordRequired"));
      if (!confirmPassword) throw new Error(t("auth.signup.errors.confirmRequired"));

      const pwErr = validatePassword(password, t);
      if (pwErr) throw new Error(pwErr);
      if (password !== confirmPassword) throw new Error(t("auth.signup.errors.passwordMismatch"));
      if (!acceptedTerms) throw new Error(t("auth.signup.termsRequiredError"));

      const result = await signup({
        fullName: fullName.trim(),
        email: email.trim(),
        country,
        password,
        confirmPassword,
        acceptedTerms: true,
      });

      if (result.needsEmailVerification) {
        const enc = encodeURIComponent(result.email || email.trim());
        navigate(`/check-email?email=${enc}`);
      } else {
        navigate("/create/language");
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || t("auth.signup.errors.failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const errorId = "signup-form-error";
  const passwordHintId = "signup-password-hint";
  const mismatchId = "signup-password-mismatch";

  const handleBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) navigate(-1);
    else navigate("/login");
  };

  return (
    <div className="relative min-h-screen bg-brandBeige flex flex-col items-center justify-center p-4 overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-no-repeat bg-center z-0"
        style={{ backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/home/sign-up.png')" }}
      />

      <main
        id="main-content"
        tabIndex={-1}
        className="relative z-10 w-full max-w-md flex flex-col outline-none"
      >
        <form
          onSubmit={handleSubmit}
          className="w-full bg-white/95 backdrop-blur-md rounded-2xl shadow-card border border-white/40 p-6"
          aria-describedby={error ? errorId : undefined}
        >
          <h1 className="text-2xl font-extrabold text-heading mb-2">{t("auth.signup.title")}</h1>
          <p className="text-body mb-5">{t("auth.signup.subtitle")}</p>

          {error && (
            <div
              id={errorId}
              role="alert"
              aria-live="polite"
              className="bg-red-100 border border-red-400 text-red-800 px-4 py-3 rounded-lg shadow-soft mb-4"
            >
              <span className="sr-only">{t("auth.signup.errorAnnouncement")}: </span>
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-heading mb-1" htmlFor="signup-name">
                {t("auth.signup.fullName")}
                <span className="text-red-700" aria-hidden="true">
                  {" "}
                  *
                </span>
                <span className="sr-only"> {t("a11y.requiredSr")}</span>
              </label>
              <input
                id="signup-name"
                className="w-full p-2 border border-gray-300 rounded-md"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={isLoading}
                autoComplete="name"
                required
                aria-invalid={error ? true : undefined}
                aria-required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-heading mb-1" htmlFor="signup-email">
                {t("auth.signup.email")}
                <span className="text-red-700" aria-hidden="true">
                  {" "}
                  *
                </span>
                <span className="sr-only"> {t("a11y.requiredSr")}</span>
              </label>
              <input
                id="signup-email"
                dir="ltr"
                className="w-full p-2 border border-gray-300 rounded-md text-left"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="email"
                required
                aria-invalid={error ? true : undefined}
                aria-required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-heading mb-1" htmlFor="signup-country">
                {t("auth.signup.country")}
                <span className="text-red-700" aria-hidden="true">
                  {" "}
                  *
                </span>
                <span className="sr-only"> {t("a11y.requiredSr")}</span>
              </label>
              <select
                id="signup-country"
                dir={uiLocale === "he" ? "rtl" : "ltr"}
                className="w-full p-2 border border-gray-300 rounded-md text-start bg-white"
                value={country}
                onChange={(e) => setCountry(e.target.value as "" | CountryValue)}
                disabled={isLoading}
                autoComplete="country"
                required
                aria-invalid={error ? true : undefined}
                aria-required
              >
                <option value="" disabled>
                  {t("auth.signup.countryPlaceholder")}
                </option>
                <option value="Israel">{t("auth.signup.countryIsrael")}</option>
                <option value="USA">{t("auth.signup.countryUSA")}</option>
                <option value="Other">{t("auth.signup.countryOther")}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-heading mb-1" htmlFor="signup-password">
                {t("auth.signup.password")}
                <span className="text-red-700" aria-hidden="true">
                  {" "}
                  *
                </span>
                <span className="sr-only"> {t("a11y.requiredSr")}</span>
              </label>
              <input
                id="signup-password"
                dir="ltr"
                className="w-full p-2 border border-gray-300 rounded-md text-left"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="new-password"
                required
                aria-invalid={password && passwordError ? true : error ? true : undefined}
                aria-describedby={
                  password && passwordError
                    ? passwordHintId
                    : confirmPassword && password !== confirmPassword
                    ? mismatchId
                    : undefined
                }
                aria-required
              />
              {password && passwordError && (
                <p id={passwordHintId} className="text-xs text-red-800 mt-1" role="status">
                  {passwordError}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-heading mb-1" htmlFor="signup-confirm">
                {t("auth.signup.confirmPassword")}
                <span className="text-red-700" aria-hidden="true">
                  {" "}
                  *
                </span>
                <span className="sr-only"> {t("a11y.requiredSr")}</span>
              </label>
              <input
                id="signup-confirm"
                dir="ltr"
                className="w-full p-2 border border-gray-300 rounded-md text-left"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="new-password"
                required
                aria-invalid={confirmPassword && password !== confirmPassword ? true : error ? true : undefined}
                aria-describedby={confirmPassword && password !== confirmPassword ? mismatchId : undefined}
                aria-required
              />
              {confirmPassword && password !== confirmPassword && (
                <p id={mismatchId} className="text-xs text-red-800 mt-1" role="status">
                  {t("auth.signup.passwordMismatchHint")}
                </p>
              )}
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3">
              <label className="flex cursor-pointer gap-3 text-start text-sm text-body leading-relaxed">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brandOrange focus:ring-brandOrange"
                  disabled={isLoading}
                />
                <span>
                  {t("auth.signup.termsAgreementPrefix")}{" "}
                  <Link
                    to="/terms"
                    className="font-semibold text-brandNavy underline underline-offset-2 hover:text-brandOrange focus:outline-none focus:ring-2 focus:ring-brandOrange rounded"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("auth.signup.termsLink")}
                  </Link>{" "}
                  {t("auth.signup.termsAnd")}{" "}
                  <Link
                    to="/privacy"
                    className="font-semibold text-brandNavy underline underline-offset-2 hover:text-brandOrange focus:outline-none focus:ring-2 focus:ring-brandOrange rounded"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("auth.signup.privacyLink")}
                  </Link>
                  {t("auth.signup.termsAgreementSuffix")}
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={
              isLoading ||
              !fullName.trim() ||
              !email.trim() ||
              !country ||
              !password ||
              !confirmPassword ||
              !acceptedTerms
            }
            className="w-full mt-6 bg-brandOrange hover:bg-orange-600 text-white font-semibold py-2 rounded-2xl shadow-card disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
          >
            {isLoading ? t("auth.signup.submitting") : t("auth.signup.submit")}
          </button>

          <p className="text-sm text-gray-600 mt-4">
            {t("auth.signup.alreadyHaveAccount")}{" "}
            <Link to="/login" className="text-brandNavy font-semibold hover:underline">
              {t("auth.signup.signIn")}
            </Link>
          </p>
        </form>

        <div className="mt-4 w-full flex justify-center">
          <button
            type="button"
            onClick={handleBack}
            disabled={isLoading}
            className="bg-brandNavy/90 hover:bg-brandNavy text-white font-semibold py-3 px-10 rounded-2xl shadow-soft transition-all duration-200 hover:shadow-card focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("wizard.common.back")}
          </button>
        </div>
      </main>

      <div className="relative z-10 mt-auto w-full max-w-md pb-4">
        <SiteFooter />
      </div>
    </div>
  );
};

export default Signup;
